import { ApolloClient, ApolloError, gql, type NormalizedCacheObject } from '@apollo/client/core';
import * as CheckoutBranding from './CheckoutBranding';
import * as CheckoutEvent from './CheckoutEvent';
import * as ShoppingCart from './ShoppingCart';
import type { Query } from '../generated/checkout.graphql';

const CHECKOUT_CONFIG_FIELDS = gql`
  fragment CheckoutConfigFields on CheckoutConfig {
    eventId
    paymentProcessor
    processorPublicKey
    processorAccountId
    donation
    accessCode
    discount
    published
    includeConvenienceFee
    includeTaxFee
    includeCcFee
    requirePhoneNumber
    hiveBrandId
    checkboxes {
      key
      label
      required
      description
      descriptionTitle
    }
    quickCheckoutTos {
      key
      label
      required
      description
      descriptionTitle
    }
    alerts {
      title
      messages
    }
    tracking {
      provider
      trackingId
    }
    features {
      feature
      enabled
    }
  }
`;

// Temp export
export const EVENT_FIELDS = gql`
  fragment EventFields on CheckoutEvent {
    doorTime
    endTime
    id
    canonicalUrl
    slug
    minimumAge
    name
    promoter
    support
    startTime
    endTime
    footerContent
    description
    usesSeatingChart
    showIceBanner
    config {
      hiveBrandId
    }
    images {
      name
      highlighted
      variants {
        src
        variant
      }
    }
    seatingChartConfig {
      workspaceKey
      eventKey
      region
      objectWithoutPricingSelectable
      publishedThumbnailUrl
      bestAvailable
    }
    venue {
      city
      country
      currency
      lat
      locale
      long
      name
      postal
      state
      street1
      street2
      timeZone
      logoUrl
      logo {
        variants {
          variant
          src
        }
      }
    }
    artists {
      name
      links {
        url
      }
    }
  }
`;

export const ORDER_SUMMARY_FIELDS = gql`
  fragment OrderSummaryFields on OrderSummary {
    fingerprint
    state
    orderConfirmationContent
    customer {
      email
      firstName
      lastName
      phoneNumber
    }
    address {
      street1
      street2
      city
      state
      postalCode
      country
    }
    event {
      ...EventFields
    }
    breakdown {
      itemsBreakdown {
        amount
        key
        label
      }
      itemsTotal
      orderTotal
      totalBreakdown {
        amount
        key
        label
      }
    }
    items {
      name
      blueprintId
      seat
      table
      discountCode
      token
      breakdown {
        basePrice
        convenienceFee
        discountAmount
        price
        total
      }
    }
  }
  ${EVENT_FIELDS}
`;

export const ORDER_SUMMARY_PRINT_TICKET_FIELDS = gql`
  fragment OrderSummaryFields on OrderSummary {
    fingerprint
    items {
      name
      token
      paperTicketHtml
    }
  }
`;

const GET_EVENT_QUERY = gql`
  query GetEvent($eventId: Int, $slug: String) {
    event(id: $eventId, slug: $slug) {
      ...EventFields
      config {
        ...CheckoutConfigFields
      }
    }
  }
  ${EVENT_FIELDS}
  ${CHECKOUT_CONFIG_FIELDS}
`;

export const getEvent = async (client: ApolloClient<NormalizedCacheObject>, eventIdOrSlug: number | string): Promise<CheckoutEvent.CheckoutEvent> => {
  let eventId = null;
  let slug = null;
  if (typeof eventIdOrSlug === 'number') {
    eventId = eventIdOrSlug;
  } else if (eventIdOrSlug.includes('-')) {
    slug = eventIdOrSlug;
  } else {
    eventId = Number(eventIdOrSlug);
    if (isNaN(eventId)) {
      throw new Error('Invalid value provided for `eventIdOrSlug`');
    }
  }

  const { data } = await client.query<Query>({
    query: GET_EVENT_QUERY,
    variables: {
      eventId,
      slug,
    },
    fetchPolicy: 'network-only',
  });

  if (!data.event) {
    throw new Error('Event not found placeholder');
  }

  return CheckoutEvent.fromGql(data.event);
};

const GET_EVENT_CANONICAL_URL_QUERY = gql`
  query GetEventCanonicalUrl($eventId: Int, $slug: String) {
    event(id: $eventId, slug: $slug) {
      id
      canonicalUrl
    }
  }
`;

/** Where an event is canonically served, for the checkout host gate; `null` when there is no such event. */
export const getEventCanonicalUrl = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventIdOrSlug: string,
  signal?: AbortSignal,
): Promise<string | null> => {
  const isSlug = eventIdOrSlug.includes('-');

  if (!isSlug && !/^\d+$/.test(eventIdOrSlug)) {
    return null;
  }

  const { data, errors } = await client.query<Query>({
    query: GET_EVENT_CANONICAL_URL_QUERY,
    variables: { eventId: isSlug ? null : Number(eventIdOrSlug), slug: isSlug ? eventIdOrSlug : null },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    context: { fetchOptions: { signal } },
  });

  // The API reports a missing event as a NOT_FOUND error, not a bare `event: null`.
  if (errors?.some(error => error.extensions?.code !== 'NOT_FOUND')) {
    throw new ApolloError({ graphQLErrors: errors });
  }

  return data?.event?.canonicalUrl ?? null;
};

export const SHOPPING_CART_FRAGMENT = gql`
  fragment ShoppingCartFields on ShoppingCart {
    token
    priceBreakdown {
      itemsBreakdown {
        amount
        key
        label
      }
      itemsTotal
      orderTotal
      totalBreakdown {
        amount
        key
        label
      }
    }
    controlQuantity
    items {
      typeHash
      name
      itemId
      itemType
      count
      amount
      details
      breakdown {
        basePrice
        price
        total
      }
    }
    lockExpiresAt
    seatsioHoldToken
  }
`;

const GET_SHOPPING_CART_QUERY = gql`
  query GetShoppingCart($eventId: Int!, $token: String) {
    shoppingCart(eventId: $eventId, token: $token) {
      ...ShoppingCartFields
    }
  }
  ${SHOPPING_CART_FRAGMENT}
`;

export const getShoppingCart = async (client: ApolloClient<NormalizedCacheObject>, token?: string, eventId?: number): Promise<ShoppingCart.ShoppingCart> => {
  const { data } = await client.query<Query>({
    query: GET_SHOPPING_CART_QUERY,
    fetchPolicy: 'network-only',
    variables: {
      token,
      eventId,
    },
  });

  return ShoppingCart.fromGql(data.shoppingCart);
};

const BRANDING_FIELDS = gql`
  fragment BrandingFields on CheckoutBranding {
    known
    brandName
    landingUrl
    attribution {
      show
      text
      url
    }
    favicons {
      rel
      sizes
      url
    }
  }
`;

const GET_BRANDING_QUERY = gql`
  query GetCheckoutBranding($host: String!) {
    branding(host: $host) {
      ...BrandingFields
    }
  }
  ${BRANDING_FIELDS}
`;

/**
 * Branding for a request host, or `undefined` when the host has no white-label config.
 *
 * Unlike its neighbours this resolves rather than throws for a missing record: `branding` is
 * non-null and carries an explicit `known` flag, so "no config for this host" is a normal answer —
 * it is the ordinary VenuePilot case and most of the traffic — rather than an error. Anything that
 * actually went wrong still rejects, and the caller classifies it as a failure.
 */
export const getBranding = async (
  // Typed on the cache shape `createApiClient` actually produces, rather than on `Query` as the
  // functions above are: the branding lookup builds its own client (it runs before the connection
  // store exists) and `Query` there stands in for a cache shape it was never meant to describe.
  client: ApolloClient<NormalizedCacheObject>,
  host: string,
  signal?: AbortSignal,
): Promise<CheckoutBranding.CheckoutBranding | undefined> => {
  const { data } = await client.query<Query>({
    query: GET_BRANDING_QUERY,
    variables: { host },
    fetchPolicy: 'network-only',
    context: { fetchOptions: { signal } },
  });

  return data.branding.known ? CheckoutBranding.fromGql(data.branding) : undefined;
};
